import React from "react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface TestStatusIconProps {
  status: "missing" | "status" | "fastfill" | "none";
  variant?: "solid" | "outline";
  size?: "sm" | "md" | "lg";
  className?: string;
}

const STATUS_LABELS = {
  missing: "Test contains compulsory data fields that have not been completed.",
  status: "The Test is currently in Archived, Draft or QA status and has not been saved as Final.",
  fastfill: "Test contains fields that include text that does not match a pre-approved Fast Fill response.",
  none: "Pass"
}

const sizeClasses = {
  sm: "w-4 h-4",
  md: "w-8 h-8", 
  lg: "w-10 h-10"
};

const getStatusColor = (variant: "solid" | "outline", status: "missing" | "status" | "fastfill" | "none") => {
  switch (status) {
    case "missing":
      return variant === "solid" ? "#FF0000" : "#FF0000";
    case "status":
      return variant === "solid" ? "#FF8811" : "#FF8811";
    case "fastfill":
      return variant === "solid" ? "#8C41D0" : "#8C41D0";
    default:
      return "#D1D5DB";
  }
};

export const TestStatusIcon: React.FC<TestStatusIconProps> = ({
  status,
  variant = "solid",
  size = "md",
  className
}) => {

  const statusColor = getStatusColor(variant, status);

  const getFillOpacity = () => {
    return variant === "solid" ? 1 : 0;
  };

  const getStrokeWidth = () => {
    return variant === "outline" ? 2 : 0;
  };

  return (
    <div
      className={cn(sizeClasses[size], className)}
      aria-label={STATUS_LABELS[status]}
      title={STATUS_LABELS[status]}
    >
      <svg
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full"
      >
        {/* Octagonal shape */}
        <path
          d="M10.343 2H21.657L30 10.343V21.657L21.657 30H10.343L2 21.657V10.343L10.343 2Z"
          fill={statusColor}
          fillOpacity={getFillOpacity()}
          stroke={statusColor}
          strokeWidth={getStrokeWidth()}
        />
        
        {/* Exclamation mark */}
        <g fill={variant === "solid" ? "white" : statusColor}>
          {/* Exclamation line */}
          <rect
            x="15"
            y="8"
            width="2"
            height="12"
            rx="1"
          />
          {/* Exclamation dot */}
          <circle
            cx="16"
            cy="24"
            r="1.5"
          />
        </g>
      </svg>
    </div>
  );
};

interface TestStatusDisplayProps {
//   statuses?: Array<{
//     id: string;
//     status: "missing" | "status" | "fastfill" | "none";
//     variant?: "solid" | "outline";
//   }>;
  size?: "sm" | "md" | "lg";
  className?: string;
  included: boolean;
  fastFillError?: boolean | null;
  missingError?: boolean | null;
  statusError?: boolean | null;
}

export function TestErrorIcons({
//   statuses = [
//     { id: "1", status: "missing", variant: "solid" },
//     { id: "2", status: "status", variant: "outline" },
//     { id: "3", status: "fastfill", variant: "solid" },
//     // { id: "4", status: "none", variant: "solid" }
//   ],
 included = true,
  fastFillError,
  missingError,
  statusError,
  size = "md",
  className
}: TestStatusDisplayProps) {
  return (

    <div
      className={cn("flex flex-row items-center gap-1 bg-white/75 rounded-md px-2 py-1", className)}
    >
      <TestStatusIcon status={missingError ? "missing" : "none"} variant={included ? "solid" : "outline"} size={size} />
      <TestStatusIcon status={fastFillError ? "fastfill" : "none"} variant={included ? "solid" : "outline"} size={size} />
      <TestStatusIcon status={statusError ? "status" : "none"} variant={included ? "solid" : "outline"} size={size} />
    </div>
  );
}

// Pure pill component (display only)
interface TestErrorPillProps extends TestStatusIconProps {
  value?: string | null;
  children?: React.ReactNode;
  tooltip?: string;
}

export const TestErrorPill = React.memo(({
  status,
  variant = "solid",
  value = null,
  size = "md",
  className,
  tooltip,
  children,
}: TestErrorPillProps) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            "flex flex-row items-center justify-center gap-1 rounded-md px-1 py-0.5 min-w-12 min-h-[36px]",
            "cursor-default",
            {
              "bg-orange-200 border border-orange-500": status === "status",
              "bg-red-200 border border-red-500": status === "missing",
              "bg-purple-200 border border-purple-500": status === "fastfill",
              "bg-transparent": status === "none",
            },
            className
          )}
        >
          {status !== "none" && (
            <>
              <TestStatusIcon status={status} variant={variant} size={size} />
              {value !== null && value !== "0" && (
                <span className="text-sm tracking-tighter font-medium">
                  {value}
                </span>
              )}
            </>
          )}
          {children}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        {tooltip || STATUS_LABELS[status]}
      </TooltipContent>
    </Tooltip>
  );
});

TestErrorPill.displayName = "TestErrorPill";

// Interactive button component
interface TestErrorPillButtonProps extends TestStatusIconProps {
  value?: number | null;
  children?: React.ReactNode;
  tooltip?: string;
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
}

export const TestErrorPillButton = React.memo(({
  status,
  variant = "solid",
  value = null,
  size = "md",
  className,
  tooltip,
  children,
  onClick,
  isActive = false,
  disabled = false,
}: TestErrorPillButtonProps) => {
  const isZeroValue = value === 0
  
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          disabled={disabled}
          type="button"
          className={cn(
            "flex flex-row items-center justify-center gap-1 rounded-md px-1 py-0.5 min-w-12 min-h-[36px]",
            "transition-all duration-200",
            {
              // Zero value - grey pill (overrides all other states)
              "bg-transparent border border-zinc-200": isZeroValue,
              
              // Inactive state (button "off")
              "border border-orange-500 bg-transparent hover:bg-orange-100": !isZeroValue && !isActive && !disabled && status === 'status',
              "border border-red-500 bg-transparent hover:bg-red-100": !isZeroValue && !isActive && !disabled && status === 'missing',
              "border border-purple-500 bg-transparent hover:bg-purple-100": !isZeroValue && !isActive && !disabled && status === 'fastfill',
              
              // Active state (button "on")
              "bg-orange-200 border border-orange-500": !isZeroValue && isActive && !disabled && status === "status",
              "bg-red-200 border border-red-500": !isZeroValue && isActive && !disabled && status === "missing",
              "bg-purple-200 border border-purple-500": !isZeroValue && isActive && !disabled && status === "fastfill",

              // Disabled state
              "bg-transparent border border-zinc-100 cursor-not-allowed": !isZeroValue && disabled,
              "cursor-pointer": !disabled && !isZeroValue,
            },
            className
          )}
        >
          <TestStatusIcon status={isZeroValue ? "none" : status} variant={variant} size={size} />
          {value !== null && (
            <span className="text-sm tracking-tighter font-medium">
              {value}
            </span>
          )}
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent>
        {tooltip || STATUS_LABELS[status]}
      </TooltipContent>
    </Tooltip>
  );
});

TestErrorPillButton.displayName = "TestErrorPillButton";

// Legacy default export - defaults to pill for backward compatibility
export default TestErrorPill;
